/**
 * UsersSettings Component
 *
 * Users management tab content.
 * Displays list of users and pending invitations in a unified table.
 *
 * All business logic (queries, mutations, modal state) lives in
 * {@link useUsersManagement} – this component is purely presentational.
 */
import { Button } from '@insio/ui-toolkit';
import { RolesGuide } from '@/components/roles-guide/RolesGuide';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { SELECTABLE_ROLES } from '../../constants/roles';
import { ConfirmActionModal, EditUserModal, InviteUserModal, UsersTable } from './users';
import { useProfile } from '@/features/profile/hooks/useProfile';
import { useUsersManagement } from '../../hooks/use-users-management';
import { boldInterpolation } from './users/users-table/user-table-helpers';

export function UsersSettings() {
  const t = useFeatureTranslation('systemSettings.users');
  const tRole = useEnumTranslation('userRole');
  const tRoleDescription = useEnumTranslation('userRoleDescription');
  const { profile } = useProfile();

  const {
    tableRows,
    loading,
    error,
    showInviteModal,
    editingUser,
    confirmAction,
    isActionLoading,
    openInviteModal,
    closeInviteModal,
    closeEditModal,
    openEditModal,
    closeConfirmAction,
    handleInviteSuccess,
    handleEditSuccess,
    handleConfirmAction,
    handleResendInvitation,
    handleDeactivate,
    handleReactivate,
    handleDelete,
    handleCancelInvitation
  } = useUsersManagement();

  const getConfirmModalProps = () => {
    if (!confirmAction) return null;

    switch (confirmAction.type) {
      case 'deactivate':
        return {
          title: t('modals.confirmDeactivate.title'),
          message: boldInterpolation(t, 'modals.confirmDeactivate.message', {
            name: confirmAction.user.displayName
          }),
          confirmLabel: t('modals.confirmDeactivate.confirm'),
          variant: 'warning' as const
        };
      case 'reactivate':
        return {
          title: t('modals.confirmReactivate.title'),
          message: boldInterpolation(t, 'modals.confirmReactivate.message', {
            name: confirmAction.user.displayName
          }),
          confirmLabel: t('modals.confirmReactivate.confirm'),
          variant: 'primary' as const
        };
      case 'delete':
        return {
          title: t('modals.confirmDelete.title'),
          message: boldInterpolation(t, 'modals.confirmDelete.message', {
            name: confirmAction.user.displayName
          }),
          confirmLabel: t('modals.confirmDelete.confirm'),
          variant: 'danger' as const
        };
      case 'cancelInvitation':
        return {
          title: t('modals.confirmCancelInvitation.title'),
          message: boldInterpolation(t, 'modals.confirmCancelInvitation.message', {
            email: confirmAction.invitation.email
          }),
          confirmLabel: t('modals.confirmCancelInvitation.confirm'),
          variant: 'danger' as const
        };
    }
  };

  return (
    <div className='space-y-lg'>
      <div className='flex items-start justify-end'>
        {/* Mobile: square icon-only button */}
        <Button
          variant='primary'
          onClick={openInviteModal}
          icon='user-plus'
          aria-label={t('actions.inviteUser')}
          className='md:hidden shrink-0 p-sm!'
        />
        {/* Desktop: full button with text */}
        <Button
          variant='primary'
          onClick={openInviteModal}
          icon='user-plus'
          className='hidden md:inline-flex shrink-0'
        >
          {t('actions.inviteUser')}
        </Button>
      </div>

      <RolesGuide
        roles={SELECTABLE_ROLES}
        label={tRole}
        description={tRoleDescription}
        testId='users-roles-guide'
      />

      <UsersTable
        rows={tableRows}
        loading={loading}
        error={error}
        currentUserId={profile?.id}
        onEdit={openEditModal}
        onDeactivate={handleDeactivate}
        onReactivate={handleReactivate}
        onDelete={handleDelete}
        onResendInvitation={handleResendInvitation}
        onCancelInvitation={handleCancelInvitation}
      />

      {/* Modals */}
      {showInviteModal && (
        <InviteUserModal onClose={closeInviteModal} onSuccess={handleInviteSuccess} />
      )}

      {editingUser && (
        <EditUserModal user={editingUser} onClose={closeEditModal} onSuccess={handleEditSuccess} />
      )}

      {confirmAction && (
        <ConfirmActionModal
          {...getConfirmModalProps()!}
          onConfirm={handleConfirmAction}
          onClose={closeConfirmAction}
          loading={isActionLoading}
        />
      )}
    </div>
  );
}
